/* File: TrashBadge.jsx
 * Path: ananunadm/src/components/trash/TrashBadge.jsx
 *
 * Header icon that shows the current count of trashed files.
 * Clicking navigates to /trash.
 * Refreshes the count after any navigation event so the badge stays live.
 */
import { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Badge, Tooltip, Button } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import * as API from "../../helpers/api";
import config from "../../config";
import { useLang } from "../../helpers/language";
import { T } from "../../helpers/translator";

const POLL_MS = 60_000; // re-fetch every 60 s

export function TrashBadge({ isMobile }) {
	const history = useHistory();
	const location = useLocation();
	const { lang } = useLang();
	const [count, setCount] = useState(null);

	const fetchCount = useCallback(async () => {
		try {
			const res = await API.get({
				route: config.api.system.upload + "-trash/stats",
			});
			setCount(Number(res?.count) || 0);
		} catch {
			// silently ignore — badge just stays hidden
		}
	}, []);

	// Fetch on mount, on every navigation, and on interval
	useEffect(() => { fetchCount(); }, [fetchCount, location.pathname]);
	useEffect(() => {
		const id = setInterval(fetchCount, POLL_MS);
		return () => clearInterval(id);
	}, [fetchCount]);

	const isActive = location.pathname === "/trash";

	return (
		<Tooltip label={T("trash", lang)} position="bottom" withinPortal>
			<Button
				variant={isActive ? "light" : "subtle"}
				size={isMobile ? "sm" : "md"}
				onClick={() => history.push("/trash")}
				aria-label={T("trash", lang)}
				style={{ position: "relative", overflow: "visible", margin: 0, padding: 10 }}
			>
				<IconTrash size={isMobile ? 18 : 20} stroke={1.5} />
				{count != null && count > 0 && (
					<Badge
						size="xs"
						color="orange"
						variant="filled"
						radius="xl"
						style={{
							position: "absolute",
							top: -4,
							right: -4,
							minWidth: 16,
							height: 16,
							padding: "0 3px",
							fontSize: 9,
							lineHeight: "16px",
							border: "2px solid var(--glass-elevated)",
							pointerEvents: "none",
						}}
					>
						{count > 99 ? "99+" : count}
					</Badge>
				)}
			</Button>
		</Tooltip>
	);
}

export default TrashBadge;
